import Vue from 'vue';
import { runtime, type Track } from './bridge';
import { searchFailures, searchDiagnosticLabel } from './search-diagnostics';
import { normalizeSearchSource, searchSourceIds } from './search-source';
import { effectiveSearchSource, workspace } from './workspace';
import { rankOnlinePage } from './search-rank';

export type EntitySearchKind = 'artists' | 'albums' | 'playlists';
export type SearchKind = 'all' | 'tracks' | 'musicVideos' | EntitySearchKind;
type CatalogEntity = { id: string | number; name: string; [key: string]: unknown };
type SearchPlatform = 'all' | 'wy' | 'tx';
type EntityPage = { cursor: string; done: boolean; total: number; error: string };
type EntityGroup = { results: CatalogEntity[]; loading: boolean; error: string; notice: string; cursor: string | null; total: number; pages: Record<'wy' | 'tx', EntityPage> };
const entityKinds: EntitySearchKind[] = ['artists', 'albums', 'playlists'];
const newPage = (): EntityPage => ({ cursor: '0', done: false, total: 0, error: '' });
const newGroup = (): EntityGroup => ({ results: [], loading: false, error: '', notice: '', cursor: null, total: 0, pages: { wy: newPage(), tx: newPage() } });

export const search = Vue.observable({
  query: '', open: false, mobileExpanded: false, composing: false, loading: false, source: workspace.defaultSearchSource, platform: 'all' as SearchPlatform, kind: 'all' as SearchKind,
  results: [] as Track[], diagnostics: [] as { id: string; status?: string; error?: string }[], cursor: null as string | null, error: '',
  entities: { artists: newGroup(), albums: newGroup(), playlists: newGroup() },
});
let timer = 0, version = 0, trackVersion = 0;
let controller: AbortController | null = null;
const entityControllers: Partial<Record<EntitySearchKind, AbortController>> = {};
const entityVersions: Record<EntitySearchKind, number> = { artists: 0, albums: 0, playlists: 0 };

export function cancelSearch() {
  clearTimeout(timer);
  version += 1;
  controller?.abort();
  search.loading = false;
  for (const kind of entityKinds) {
    entityControllers[kind]?.abort();
    search.entities[kind].loading = false;
  }
}
export function closeSearch() { cancelSearch(); search.open = false; search.mobileExpanded = false; }
export function composeStart() { search.composing = true; cancelSearch(); }
export function composeEnd(value: string) { search.composing = false; setQuery(value); }
export function setQuery(value: string, reveal = true) {
  search.source = normalizeSearchSource(search.source);
  search.query = value;
  search.open = reveal && Boolean(value.trim());
  cancelSearch();
  if (search.composing) return;
  search.results = [];
  runtime.state.remoteSearchResults = [];
  search.error = '';
  search.cursor = null;
  for (const kind of entityKinds) Object.assign(search.entities[kind], newGroup());
  if (value.trim()) timer = window.setTimeout(() => void submitSearch(false, reveal), 300);
}

export async function submitEntitySearch(kind: EntitySearchKind, append = false) {
  if (search.composing || !search.query.trim()) return;
  const group = search.entities[kind];
  const generation = version, request = ++entityVersions[kind], query = search.query.trim();
  entityControllers[kind]?.abort();
  group.error = '';
  group.notice = '';
  if (!append) Object.assign(group, newGroup());
  if (effectiveSearchSource(search.source) === 'local') { group.loading = false; return; }
  const active = new AbortController();
  entityControllers[kind] = active;
  group.loading = true;
  const isCurrent = () => generation === version && request === entityVersions[kind] && search.query.trim() === query;
  try {
    const platforms: ('wy' | 'tx')[] = search.platform === 'all' ? ['tx', 'wy'] : [search.platform];
    const incoming = await Promise.all(platforms.filter(platform => !group.pages[platform].done).map(async platform => {
      const page = group.pages[platform];
      try {
        const params = new URLSearchParams({ kind: 'search', platform, type: kind, q: query, offset: page.cursor, limit: '12' });
        const payload = await runtime.api('/api/catalog?' + params, { signal: active.signal });
        if (!isCurrent()) return [];
        if (!Array.isArray(payload[kind])) throw new Error('来源未返回有效的目录搜索结果。');
        page.error = '';
        page.total = Number.isFinite(payload.total) ? payload.total : payload[kind].length;
        page.done = !payload.more || payload.nextCursor == null;
        if (!page.done) page.cursor = String(payload.nextCursor);
        return payload[kind].map((item: CatalogEntity) => ({ ...item, platform }));
      } catch (error) {
        if (isCurrent() && (error as Error).name !== 'AbortError') page.error = (error as Error).message;
        return [];
      }
    }));
    if (!isCurrent()) return;
    group.results = [...new Map([...group.results, ...incoming.flat()].map(item => [`${item.platform}:${item.id}`, item])).values()];
    group.total = platforms.reduce((sum, platform) => sum + group.pages[platform].total, 0);
    group.error = platforms.filter(platform => group.pages[platform].error).map(platform => `${platform === 'tx' ? 'QQ音乐' : '网易云音乐'}：${group.pages[platform].error}`).join('；');
    group.cursor = platforms.some(platform => !group.pages[platform].done) ? 'more' : null;
  } catch (error) {
    if (isCurrent() && (error as Error).name !== 'AbortError') group.error = (error as Error).message;
  } finally {
    if (isCurrent()) group.loading = false;
  }
}

export async function submitSearch(append = false, reveal = true) {
  if (search.composing || !search.query.trim()) return;
  search.source = normalizeSearchSource(search.source);
  if (append) controller?.abort();
  else {
    cancelSearch();
    search.results = [];
    runtime.state.remoteSearchResults = [];
    search.cursor = null;
    for (const kind of entityKinds) void submitEntitySearch(kind);
  }
  const generation = version, request = ++trackVersion, query = search.query.trim();
  if (reveal) search.open = true;
  search.loading = true;
  search.error = '';
  search.diagnostics = [];
  const scope = effectiveSearchSource(search.source);
  const sources = searchSourceIds(scope, runtime.ui.sourcePreferences).join(',');
  if (scope === 'local' || !sources) { search.loading = false; return; }
  const active = new AbortController();
  controller = active;
  const isCurrent = () => generation === version && request === trackVersion && search.query.trim() === query;
  try {
    const requestedCursor = append ? search.cursor || '0' : '0';
    const params = new URLSearchParams({ q: query, sources, platform: search.platform, type: 'track', cursor: requestedCursor });
    const payload = await runtime.api('/api/search?' + params, { signal: active.signal });
    if (!isCurrent()) return;
    const normalized = (payload.results || []).map(runtime.normalizeTrack) as Track[];
    const incoming = workspace.appearance === 'salt' ? rankOnlinePage(normalized, query, search.platform) : normalized;
    search.results = append ? [...new Map([...search.results, ...incoming].map(track => [track.id, track])).values()] : incoming;
    runtime.state.remoteSearchResults = search.results;
    search.diagnostics = (payload.sources || []).map((source: any) => ({ ...source, id: source.id || source.sourceId }));
    const failures = searchFailures(search.diagnostics);
    // Keep a partially failed page retryable. Advancing the shared upstream
    // offset here would permanently skip the missing platform's recordings.
    search.cursor = failures.length ? requestedCursor : payload.nextCursor || null;
    if (!search.results.length && failures.length) search.error = failures.map(source => `${searchDiagnosticLabel(source.id)}：${source.error}`).join('；');
  } catch (error) {
    if (isCurrent() && (error as Error).name !== 'AbortError') search.error = (error as Error).message;
  } finally {
    if (isCurrent()) search.loading = false;
  }
}
